
import { useState } from 'react';

interface BusinessProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BusinessProfileModal({ isOpen, onClose }: BusinessProfileModalProps) {
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [companyIntro, setCompanyIntro] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Validation functions
  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'companyName':
        if (value.length < 2) return 'Company name must be at least 2 characters';
        if (value.toLowerCase().includes('test') || value.toLowerCase().includes('example')) {
          return 'Please enter your actual company name (not test data)';
        }
        return '';
      case 'contactPerson':
        if (value.length < 2) return 'Contact person must be at least 2 characters';
        if (!value.includes(' ')) return 'Please enter full name (first and last name)';
        if (value.toLowerCase().includes('test') || value.toLowerCase().includes('example')) {
          return 'Please enter the actual contact person name (not test data)';
        }
        return '';
      case 'contactEmail':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        if (value.includes('test') || value.includes('example')) {
          return 'Please enter your actual business email (not test data)';
        }
        return '';
      case 'contactPhone':
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
        if (!phoneRegex.test(value.replace(/[\s\-\(\)]/g, ''))) {
          return 'Please enter a valid phone number';
        }
        if (value.includes('123') || value.includes('000')) {
          return 'Please enter your actual phone number (not placeholder data)';
        }
        return '';
      case 'companyIntro':
        if (value.length < 50) return 'Company introduction must be at least 50 characters';
        if (value.length < 100) return 'Please provide more detailed company information (at least 100 characters)';
        if (value.toLowerCase().includes('test') || value.toLowerCase().includes('example') || 
            value.toLowerCase().includes('lorem ipsum')) {
          return 'Please enter actual company information (not placeholder text)';
        }
        return '';
      default:
        return '';
    }
  };

  const validateAllFields = () => {
    const errors: Record<string, string> = {};
    
    errors.companyName = validateField('companyName', companyName);
    errors.contactPerson = validateField('contactPerson', contactPerson);
    errors.contactEmail = validateField('contactEmail', contactEmail);
    errors.contactPhone = validateField('contactPhone', contactPhone);
    errors.companyIntro = validateField('companyIntro', companyIntro);

    // Remove empty error messages
    Object.keys(errors).forEach(key => {
      if (!errors[key]) delete errors[key];
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldChange = (name: string, value: string) => {
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    switch (name) {
      case 'companyName':
        setCompanyName(value);
        break;
      case 'contactPerson':
        setContactPerson(value);
        break;
      case 'contactEmail':
        setContactEmail(value);
        break;
      case 'contactPhone':
        setContactPhone(value);
        break;
      case 'website':
        setWebsite(value);
        break;
      case 'companyIntro':
        setCompanyIntro(value);
        break;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    // Validate all fields first
    if (!validateAllFields()) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Please fix the highlighted issues above before submitting.' 
      });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formData = new FormData();
      formData.append('company_name', companyName);
      formData.append('contact_person', contactPerson);
      formData.append('contact_email', contactEmail);
      formData.append('contact_phone', contactPhone);
      formData.append('website', website);
      formData.append('company_introduction', companyIntro);

      const response = await fetch('https://readdy.ai/api/form/d3u4b4djnnh8vq2jut90', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Profile submitted successfully!' });
        setTimeout(() => {
          setCompanyName('');
          setContactPerson('');
          setContactEmail('');
          setContactPhone('');
          setWebsite('');
          setCompanyIntro('');
          setSubmitStatus(null);
          setFieldErrors({});
          onClose();
        }, 1200);
      } else {
        const text = await response.text().catch(() => '');
        let errorMessage = 'Failed to submit profile. Please try again.';
        
        if (text.includes('spam')) {
          errorMessage = 'Your submission was flagged as spam. Please ensure all information is genuine and detailed, then try again.';
        } else if (text.includes('InvalidParameter')) {
          errorMessage = 'Some information appears invalid. Please check all fields contain real, detailed business information.';
        }
        
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'Failed to submit profile. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Submit Your Business Profile</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isSubmitting}
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                id="companyName"
                name="company_name"
                value={companyName}
                onChange={(e) => handleFieldChange('companyName', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  fieldErrors.companyName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your actual company name"
                required
                disabled={isSubmitting}
              />
              {fieldErrors.companyName && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.companyName}</p>
              )}
            </div>

            <div>
              <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
                Contact Person *
              </label>
              <input
                type="text"
                id="contactPerson"
                name="contact_person"
                value={contactPerson}
                onChange={(e) => handleFieldChange('contactPerson', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  fieldErrors.contactPerson ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter full name (first and last name)"
                required
                disabled={isSubmitting}
              />
              {fieldErrors.contactPerson && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.contactPerson}</p>
              )}
            </div>

            <div>
              <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Business Email *
              </label>
              <input
                type="email"
                id="contactEmail"
                name="contact_email"
                value={contactEmail}
                onChange={(e) => handleFieldChange('contactEmail', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  fieldErrors.contactEmail ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your business email address"
                required
                disabled={isSubmitting}
              />
              {fieldErrors.contactEmail && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.contactEmail}</p>
              )}
            </div>

            <div>
              <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="contactPhone"
                name="contact_phone"
                value={contactPhone}
                onChange={(e) => handleFieldChange('contactPhone', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  fieldErrors.contactPhone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your business phone number"
                required
                disabled={isSubmitting}
              />
              {fieldErrors.contactPhone && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.contactPhone}</p>
              )}
            </div>

            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                Website (Optional)
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={website}
                onChange={(e) => handleFieldChange('website', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://www.yourcompany.com"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="companyIntro" className="block text-sm font-medium text-gray-700 mb-2">
                Company Introduction * (Minimum 100 characters)
              </label>
              <textarea
                id="companyIntro"
                name="company_introduction"
                value={companyIntro}
                onChange={(e) => handleFieldChange('companyIntro', e.target.value.slice(0, 500))}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-32 resize-none ${
                  fieldErrors.companyIntro ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Provide a detailed description of your company, including what you do, your target market, key services/products, and what makes you unique. Be specific and professional."
                required
                disabled={isSubmitting}
                maxLength={500}
              />
              <div className="flex justify-between items-start mt-1">
                <div>
                  {fieldErrors.companyIntro && (
                    <p className="text-sm text-red-600">{fieldErrors.companyIntro}</p>
                  )}
                </div>
                <p className="text-sm text-gray-500">{companyIntro.length}/500</p>
              </div>
            </div>

            {submitStatus && (
              <div
                className={`p-4 rounded-lg ${
                  submitStatus.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {submitStatus.message}
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed whitespace-nowrap"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
